import { getByID } from '@adornis/baseql/operations/mongo.js';
import { type BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { countWaitingByWorkshop } from '@adornis/buchungsheld-db/api/participation.js';
import { BHFilter } from '@adornis/buchungsheld-db/entities/bh-filter.js';
import { Institution } from '@adornis/buchungsheld-db/entities/institution.js';
import { User } from '@adornis/buchungsheld-db/entities/user.js';
import { Workshop } from '@adornis/buchungsheld-db/entities/workshop.js';
import { Role } from '@adornis/buchungsheld-db/enums/roles.js';
import { dataviewEmptyState, productPrice } from '@adornis/buchungsheld-db/global-helpers.js';
import { getPluginForScope } from '@adornis/buchungsheld-db/plugins/plugin.js';
import type { BHTranslationDictionary } from '@adornis/buchungsheld-db/translations/translation.js';
import '@adornis/buchungsheld-theme/components/tr-button-small.js';
import '@adornis/buchungsheld-theme/components/tr-checkbox.js';
import '@adornis/buchungsheld-theme/components/tr-loader.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-empty-placeholder.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-grid.js';
import '@adornis/chemistry/elements/components/x-hr.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import '@adornis/cms/client/generated-form.js';
import { baseConfig } from '@adornis/config/baseConfig.js';
import { buildFullOptions, type DataViewOptions } from '@adornis/data-view/client/data-view-options.js';
import { View } from '@adornis/data-view/db/view.js';
import { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { until } from 'lit/directives/until.js';
import { DateTime } from 'luxon';

@customElement('bh-page-profile-events-lecturer')
export class BHProfileEventsLecturer extends ChemistryLitElement {
  @state() readonly _translation = new TranslationController<BHTranslationDictionary>(this);

  @state() private readonly _user = new RXController(this, currentUser<User>());

  @property({ type: Object }) selectedWorkshop;

  get institutionIDs() {
    const user = this._user.value ?? new User({});
    return user.roles.filter(r => r.name === Role.LECTURER).flatMap(r => r.contexts);
  }

  override render() {
    return html`
      <x-flex space="lg">
        ${this._renderCalenderLinkField()}

        <x-flex horizontal space="md" space-between crossaxis-center wrap>
          <x-h1> ${this._translation.translate('PROFILE', 'OVERVIEW_TEACHINGS', 'TITLE')} </x-h1>
        </x-flex>
        ${this.renderTeachingTable(false)}

        <br />

        ${this.renderTeachingTable(true)}
      </x-flex>
    `;
  }

  private _renderCalenderLinkField() {
    const calendarlink = `${baseConfig?.get('ROOT_URL')}calendar/lecturer/${this._user.value?._id}.ics`;

    return html`
      <tr-flex vertical crossaxis-center space="xs" wrap ${css({ alignItems: 'start' })}>
        <tr-text>${this._translation.translate('PROFILE', 'icsLinkNote')}</tr-text>
        <tr-input
          flex
          ${css({ cursor: 'pointer', width: '100%' })}
          disabled
          .value=${calendarlink}
          icon="content_copy"
          @click=${() => {
            void navigator.clipboard.writeText(calendarlink);
            return XSnackbar.show(this._translation.translate('copiedLink'));
          }}
        ></tr-input>
      </tr-flex>
    `;
  }

  renderTeachingTable(showFinished: Boolean) {
    const workshopFilter = new BHFilter({
      lecturerID: this._user.value?._id ?? '',
      aspects: [],
      searchFields: ['name', 'description'],
      sortingDirections: ['asc'],
      sortingFields: ['firstAppointment'],
    });

    const headingTranslationKey = showFinished ? 'finishedPlural' : 'currentPlural';

    // Build filter based on whether the event is already over
    // To that end: max(appointments -> end -> date) < currentTime
    // -> Prob do this in the subscription, but we need to save the info if before or after in the filter I guess?
    // render the dataView
    // columns: chronologically sorted by first appointment, institution

    const { dataViewOptions, filterOptions } = buildFullOptions({
      class: Workshop,
      operation: showFinished ? 'subscribeFinishedWorkshopsForLecturer' : 'subscribeCurrentWorkshopsForLecturer',
      isSortable: true,
      isColumnSelectable: true,
      emptyState: () => dataviewEmptyState({}),
      view: new View({ filter: workshopFilter }),
      customColumns: [
        {
          fieldName: 'institutionID',
          isSortable: true,
          columnName: 'Institution',
          columnWidth: '10ch',
          renderItem: doc => {
            const selection = {
              _id: 1,
              name: 1,
            } as const satisfies BaseQLSelectionSet<Institution>;
            const institution = getByID(Institution, doc.institutionID)(selection);
            return html`${until(
              institution.then(inst => {
                return html`<x-text>${inst?.name}</x-text>`;
              }),
            )}`;
          },
        },
        { fieldName: 'name', isSortable: true, columnName: 'Name', columnWidth: '36ch' },
        { fieldName: 'category.name', columnName: 'Kategorie', columnWidth: '10ch' },
        {
          // This fieldname because we need a fieldname that is already present in the Workshop object
          fieldName: 'appointments',
          isSortable: true,
          columnName: 'Start',
          columnWidth: '10ch',
          renderItem: doc => {
            const appointmentStarts = doc.appointments
              .filter(appointment => !!appointment.start)
              .map(appointment => appointment.start!.toMillis());
            const firstAppointment = Math.min(...appointmentStarts);
            const firstAppointmentDateTime = DateTime.fromMillis(firstAppointment);
            return html`<x-text>${firstAppointmentDateTime.toLocaleString()}</x-text>`;
          },
        },
        {
          columnName: 'Preis',
          fieldName: 'price',
          isSortable: true,
          columnWidth: 'max-content',
          selectionSet: { hasPrice: 1, prices: { _id: 1, unitAmount: 1 } },
          renderItem: instance => html` <tr-text>${productPrice(instance)}</tr-text> `,
        },
        {
          columnName: '#TN',
          fieldName: 'participants',
          columnWidth: '10ch',
          selectionSet: { capacity: 1, leftCapacity: 1, highPriorityParticipationCount: 1, institutionID: 1 },
          renderItem: doc => {
            const segment = (count, capacity) => {
              const ratio = count / capacity;
              const backgroundColor = (() => {
                if (Number.isNaN(ratio)) return 'lightgray';
                if (ratio < 0.4) return '#bc1b1b';
                if (ratio < 0.7) return '#ffa422';
                return '#39db5c';
              })();

              return html`
                <x-flex horizontal space="xs" crossaxis-center ${css({ justifyContent: 'end' })}>
                  <x-text ${css({ textAlign: 'right' })}> ${count} von ${doc.capacity} </x-text>
                  <span
                    ${css({
                      display: 'inline-block',
                      border: '1px solid lightgray',
                      borderRadius: '50%',
                      width: '12px',
                      height: '12px',
                      backgroundColor,
                    })}
                  ></span>
                </x-flex>
              `;
            };

            return html`
              <x-flex vertical space="xs">
                ${segment(doc.capacity - doc.leftCapacity, doc.capacity)}
                ${(() => {
                  const plugin = getPluginForScope(
                    'registration::product-waitinglist-priority-rules',
                    doc.institutionID,
                  );
                  if (plugin.id !== 'registration::product-waitinglist-priority-rules::icsrm') return nothing;
                  return segment(doc.highPriorityParticipationCount, doc.capacity);
                })()}
              </x-flex>
            `;
          },
        },
        {
          columnName: 'Wartend',
          customID: 'numWaiting',
          columnWidth: 'max-content',
          renderItem: doc => {
            const countWaiting = countWaitingByWorkshop(doc._id)();
            return html`${until(
              countWaiting.then(count => {
                return html` <x-text ${css({ textAlign: 'center' })}> ${count} </x-text> `;
              }),
              html`<x-text>${this._translation.translate('loading')}...</x-text>`,
            )}`;
          },
        },
        {
          columnName: '',
          customID: '_options',
          renderItem: doc =>
            html`
              <x-flex space="xs" horizontal crossaxis-start>
                <tr-button-small
                  icon="description"
                  .action=${() => this.emitEvent('workshop-notes-clicked', { workshopID: doc._id })}
                  .color=${this.colors.secondary.shade(200)}
                ></tr-button-small>

                <tr-button-small
                  icon="more_horiz"
                  .action=${() => this.emitEvent('workshop-selected', { workshopID: doc._id })}
                  .color=${this.colors.secondary.shade(200)}
                ></tr-button-small>
              </x-flex>
            `,
          columnWidth: 'max-content',
        },
      ],
    } satisfies DataViewOptions<typeof Workshop>);

    return html`
      <x-flex space="xl">
        <x-flex space="md">
          <x-flex space-between space="md" wrap horizontal crossaxis-center>
            <x-h3> ${this._translation.translate('workshop', headingTranslationKey)} </x-h3>
          </x-flex>
          <x-filter-editor .options=${filterOptions}></x-filter-editor>
          <x-data-view .options=${dataViewOptions}> </x-data-view>
        </x-flex>
      </x-flex>
    `;
  }

  emitEvent(event: string, data: any) {
    const eventToDispatch = new CustomEvent(event, { detail: data });
    this.dispatchEvent(eventToDispatch);
  }
}
